import {useMemo} from 'react'
import {TKey, useTranslation} from '@/translations/i18n'
import {createDateTime, formatDate, formatDisplayDate, formatDisplayTime} from '@/dayjs'
import dayjs from 'dayjs'

interface DisabledSlot {
    start: Date
    end: Date
}

interface UseAvailabilityWarningsProps {
    allDates?: Array<{date: Date; time: string}>
    targetDate?: Date
    selectedTime?: string
    consultantUnavailableDates: Date[]
    patientUnavailableDates: Date[]
    consultantDisabledSlots: DisabledSlot[]
    patientDisabledSlots: DisabledSlot[]
    patients?: Array<{id: string}>
    skipDatesCheck?: Date[]
    allMonthDisabled?: boolean
    duration?: number
}

// Date/time utility functions
const isSameDay = (date1: Date, date2: Date): boolean => {
    return formatDate(date1) === formatDate(date2)
}

// Conflict detection helpers
const isTimeSlotOverlapping = (selectedDateTime: Date, slot: DisabledSlot, duration?: number): boolean => {
    if (!duration) {
        return selectedDateTime >= slot.start && selectedDateTime < slot.end
    }

    // Calculate the end time of the event
    const eventEndTime = dayjs(selectedDateTime).add(duration, 'minutes').toDate()

    // Check if there's any overlap between the event and the unavailable slot
    return (
        (selectedDateTime >= slot.start && selectedDateTime < slot.end) || // Event starts during unavailable slot
        (eventEndTime > slot.start && eventEndTime <= slot.end) || // Event ends during unavailable slot
        (selectedDateTime <= slot.start && eventEndTime >= slot.end) // Event completely encompasses unavailable slot
    )
}

const hasPatients = (patients?: Array<{id: string}>): boolean => {
    return Boolean(patients?.length)
}

/**
 * Checks if target date falls on consultant unavailable dates
 */
const checkConsultantUnavailableDates = (
    targetDate: Date | undefined,
    targetTime: string | undefined,
    userUnavailableDates: Date[],
    userUnavailableSlots: DisabledSlot[],
    t: ReturnType<typeof useTranslation>['t'],
    skipDatesCheck?: Date[],
    allMonthDisabled?: boolean,
    duration?: number
): string[] => {
    if (allMonthDisabled) return [t('agenda:not_available_consultant')]

    if (!targetDate || userUnavailableDates.length === 0) return []

    // Skip warning if this date should be ignored
    const shouldSkipCheck = skipDatesCheck?.some(skipDate => isSameDay(skipDate, targetDate))
    if (shouldSkipCheck) return []

    const isUnavailable = userUnavailableDates.some(disabledDate => isSameDay(disabledDate, targetDate))
    const isUnavailableSlot = userUnavailableSlots.some(
        disabledSlot =>
            targetTime && isTimeSlotOverlapping(createDateTime(targetDate, targetTime), disabledSlot, duration)
    )

    if (!isUnavailable && !isUnavailableSlot) return [] // If the date is not unavailable and the slot is not unavailable, return an empty array

    return [
        isUnavailableSlot
            ? t('agenda:not_available_consultant_slot', {
                  day: formatDisplayDate(targetDate),
                  time: targetTime || formatDisplayTime(targetDate)
              })
            : formatDisplayTime(targetDate) !== '00:00'
              ? t('agenda:not_available_consultant_slot', {
                    day: formatDisplayDate(targetDate),
                    time: targetTime || formatDisplayTime(targetDate)
                })
              : t('agenda:not_available_consultant_day', {
                    day: formatDisplayDate(targetDate)
                })
    ]
}

/**
 * Checks if target date falls on patient absence dates
 */
const checkPatientAbsenceDates = (
    targetDate: Date | undefined,
    selectedTime: string | undefined,
    patientAbsences: Date[],
    patients: Array<{id: string}> | undefined,
    t: ReturnType<typeof useTranslation>['t']
): string[] => {
    if (!targetDate || !hasPatients(patients) || patientAbsences.length === 0) return []

    const isPatientUnavailable = patientAbsences.some(absenceDate => {
        return isSameDay(absenceDate, targetDate)
    })

    if (!isPatientUnavailable) return []

    return [
        t('agenda:patient_not_available', {
            day: formatDisplayDate(targetDate),
            time: selectedTime || formatDisplayTime(targetDate)
        })
    ]
}

/**
 * Generic function to check for time slot conflicts across multiple dates
 */
const checkSlotConflicts = (
    allDates: Array<{date: Date; time: string}> | undefined,
    disabledSlots: DisabledSlot[],
    warningKey: TKey,
    t: ReturnType<typeof useTranslation>['t'],
    skipDatesCheck?: Date[],
    duration?: number
): string[] => {
    if (!allDates || disabledSlots.length === 0) return []

    const warnings: string[] = []

    allDates.forEach(dateItem => {
        if (!dateItem.date || !dateItem.time) return

        // Skip warning if this date should be ignored
        const shouldSkipCheck = skipDatesCheck?.some(skipDate => isSameDay(skipDate, dateItem.date))
        if (shouldSkipCheck) return

        const selectedDateTime = createDateTime(dateItem.date, dateItem.time)

        disabledSlots.forEach(slot => {
            const isSameDayConflict = isSameDay(selectedDateTime, slot.start)
            const hasTimeOverlap = isTimeSlotOverlapping(selectedDateTime, slot, duration)

            if (isSameDayConflict && hasTimeOverlap) {
                warnings.push(
                    t(warningKey, {
                        day: formatDisplayDate(dateItem.date),
                        time: dateItem.time
                    })
                )
            }
        })
    })

    return warnings
}

/**
 * Checks for consultant time slot conflicts across all selected dates
 */
const checkConsultantSlotConflicts = (
    allDates: Array<{date: Date; time: string}> | undefined,
    consultantDisabledSlots: DisabledSlot[],
    t: ReturnType<typeof useTranslation>['t'],
    skipDatesCheck?: Date[],
    duration?: number
): string[] => {
    return checkSlotConflicts(
        allDates,
        consultantDisabledSlots,
        'agenda:not_available_consultant_slot',
        t,
        skipDatesCheck,
        duration
    )
}

/**
 * Checks for patient time slot conflicts across all selected dates
 */
const checkPatientSlotConflicts = (
    allDates: Array<{date: Date; time: string}> | undefined,
    patientDisabledSlots: DisabledSlot[],
    patients: Array<{id: string}> | undefined,
    t: ReturnType<typeof useTranslation>['t'],
    duration?: number
): string[] => {
    if (!hasPatients(patients)) return []

    return checkSlotConflicts(allDates, patientDisabledSlots, 'agenda:patient_not_available', t, undefined, duration)
}

/**
 * Custom hook that aggregates all availability warning checks
 * Returns an array of translated warning messages for UI display
 */
export const useAvailabilityWarnings = ({
    allDates,
    targetDate,
    selectedTime,
    consultantUnavailableDates,
    patientUnavailableDates,
    consultantDisabledSlots,
    patientDisabledSlots,
    skipDatesCheck,
    patients,
    allMonthDisabled,
    duration
}: UseAvailabilityWarningsProps): string[] => {
    const {t} = useTranslation()

    const warnings = useMemo(() => {
        const allWarnings: string[] = []

        // Aggregate all warning types using spread operator for clean composition
        allWarnings.push(
            ...checkConsultantUnavailableDates(
                targetDate,
                selectedTime,
                consultantUnavailableDates,
                consultantDisabledSlots,
                t,
                skipDatesCheck,
                allMonthDisabled,
                duration
            ),
            ...checkPatientAbsenceDates(targetDate, selectedTime, patientUnavailableDates, patients, t),
            ...checkConsultantSlotConflicts(allDates, consultantDisabledSlots, t, skipDatesCheck, duration),
            ...checkPatientSlotConflicts(allDates, patientDisabledSlots, patients, t, duration)
        )

        // Remove duplicates using Set
        return [...new Set(allWarnings)]
    }, [
        allDates,
        targetDate,
        selectedTime,
        consultantUnavailableDates,
        patientUnavailableDates,
        consultantDisabledSlots,
        patientDisabledSlots,
        patients,
        duration
    ])

    return warnings
}
